/**
 * Bank-file imports (TMMAPP-0013, design §9 / §15.1) — wire types from `src/api/generated/`;
 * normalized into the module's domain type. The SPA never parses the file; every preview is a
 * server round-trip. Upload is multipart `FormData` with the browser's boundary.
 */
import type { AxiosRequestConfig } from 'axios';
import { request } from './client';
import type {
  ColumnMap as WireColumnMap,
  ImportBatchResponse as WireBatch,
  ImportBatchRowResponse as WireRow,
  ImportCommitRequest,
  ImportDetected as WireDetected,
  ImportFit as WireFit,
  ImportFxPair as WireFxPair,
  ImportOpeningCheck as WireOpeningCheck,
  ImportPreviewRequest,
  ImportPreviewResponse as WirePreview,
  ImportProposedMapping as WireMapping,
  ImportRowSpan as WireRowSpan,
  ImportSuggestedOpening as WireSuggestedOpening,
  ImportSummary as WireSummary,
  ImportUploadResponse as WireUpload,
  PageResponseImportBatchRowResponse as WireRowPage,
} from './generated';

export type ImportBatchStatus = 'UPLOADED' | 'PREVIEWED' | 'COMMITTED' | 'FAILED' | 'DISCARDED';
export type ImportRowOutcome = 'IMPORTED' | 'DUPLICATE' | 'REJECTED' | 'BEFORE_OPENING';
export type AmountMode = 'SIGNED' | 'DEBIT_CREDIT';
export type OutflowSign = 'NEGATIVE' | 'POSITIVE';
export type MappingSource = 'PROFILE' | 'PRESET' | 'AI' | 'HEURISTIC' | 'NONE';
export type UncoveredPlanned = 'WIPE' | 'KEEP';

export interface ColumnMap {
  date: number | null;
  amount: number | null;
  debit: number | null;
  credit: number | null;
  payee: number | null;
  memo: number | null;
  runningBalance: number | null;
  externalId: number | null;
}

export interface ImportDetected {
  delimiter: string;
  encoding: string;
  headerCandidates: string[];
  /**
   * The file's rows from the detected header on, so `sampleRows[0]` is the detected header.
   * Rows before it are `preambleSample`, numbered from `preambleFrom` (TMMAPP-0130 item 9).
   */
  sampleRows: string[][];
  /** Parsed rows above the detected header (the upload's or batch's `preambleRows`). */
  preambleRows: number;
  /** The last rows before the detected header, at most 20. Empty when none were served. */
  preambleSample: string[][];
  /** 1-based file row of `preambleSample[0]`. The detected header row when the sample is empty. */
  preambleFrom: number;
}

export interface ImportProposedMapping {
  source: MappingSource;
  confidence: number | null;
  headerRow: number;
  firstDataRow: number;
  columnMap: ColumnMap;
  amountMode: AmountMode;
  outflowSign: OutflowSign;
  dateFormat: string;
  notes: string[];
}

export interface ImportUpload {
  batchId: number;
  detected: ImportDetected;
  proposedMapping: ImportProposedMapping;
  suggestedProfileId: number | null;
  /** The suggested profile's name (TMMAPI-0152 ask 7); null when there is none. */
  suggestedProfileName: string | null;
  /** TMMAPI-0149: the ACTIVE preset proposed (source PRESET), when no profile of the person's matched. */
  suggestedPresetId: number | null;
  suggestedPresetName: string | null;
  /**
   * TMMAPI-0149: present when the source is PROFILE or PRESET and the server ran the preview with
   * that mapping. Null means the mapping couldn't take the file: the mapping screen opens.
   */
  fit: ImportFit | null;
  preview: ImportPreview | null;
}

export interface ImportFitReason {
  /** MAPPED_COLUMN_MISSING, NO_ROWS, ROWS_REJECTED, DATES_IMPLAUSIBLE, BALANCE_MISMATCH, SIGN_INVERTED, ALL_BEFORE_OPENING, BEFORE_OPENING — or a newer one. */
  code: string;
  count: number | null;
  /** For MAPPED_COLUMN_MISSING, the canonical field whose column is missing; for ALL_BEFORE_OPENING, the opening date. */
  detail: string | null;
}

/** TMMAPI-0149: GOOD goes straight to the results; POOR is a friendly stop built from the reasons. */
export interface ImportFit {
  status: 'GOOD' | 'POOR';
  reasons: ImportFitReason[];
}

/** Over every row that parses. `outMinor` is the outflows' magnitude (positive). */
export interface ImportSummary {
  firstDate: string | null;
  lastDate: string | null;
  inMinor: number;
  outMinor: number;
}

export interface ImportRowError {
  row: number;
  reason: string;
}

export interface ImportParsedSample {
  postedOn: string;
  amountMinor: number;
  payee: string;
  memo: string | null;
  runningBalanceMinor: number | null;
  externalId: string | null;
}

export interface ImportPlannedCovered {
  count: number;
  /** The latest line the file would import; null when none would (TMMAPI-0176 §H). */
  coveredThrough: string | null;
}

export interface ImportFxPair {
  base: string;
  quote: string;
  rate: number;
  asOf: string;
  fxRateId: number;
}

/**
 * The opening balance the file suggests (TMMAPI-0160 U2), served only while the account's opening is
 * pending. MM-D-14 signs: a card's balance owed is negative. `on` is a civil date.
 */
export interface ImportSuggestedOpening {
  amountMinor: number;
  on: string;
  source: NonNullable<WireSuggestedOpening['source']>;
}

/** Why the file's balances are in question (TMMAPI-0160 U2). */
export type ImportSuggestedOpeningIssue = NonNullable<WirePreview['suggestedOpeningIssue']>;

/** New rows on one side of the account's opening date (TMMAPI-0191): dates absent when count is 0. */
export interface ImportRowSpan {
  count: number;
  from: string | null;
  to: string | null;
}

export type ImportOpeningCheckStatus = 'MATCHES' | 'DIFFERS' | 'NO_BANK_FIGURE';

/**
 * The opening the earlier history would move to (TMMAPI-0191 item 3), checked against the file's own
 * starting balance. Minor units in the account's currency (bank side, MM-D-63); `proposedOn` is a
 * civil date. `differenceMinor` is the ledger less the bank, on `DIFFERS`.
 */
export interface ImportOpeningCheck {
  proposedOn: string;
  proposedMinor: number;
  bankStatedMinor: number | null;
  status: ImportOpeningCheckStatus;
  differenceMinor: number | null;
}

export interface ImportPreview {
  rowsTotal: number;
  /** The newer rows only (after the opening date); `newCount` is the total to import. */
  wouldImport: number;
  /** TMMAPI-0191: the total the commit imports by default, earlier and newer together. */
  newCount: number;
  /** TMMAPI-0191: the new rows on or before the opening date (earlier history). */
  earlier: ImportRowSpan;
  /** TMMAPI-0191: the new rows after the opening date (the same rows as `wouldImport`). */
  newer: ImportRowSpan;
  /** TMMAPI-0191: null when there's no earlier history, while the opening is pending, or an unknown status. */
  openingCheck: ImportOpeningCheck | null;
  duplicates: number;
  errors: ImportRowError[];
  sample: ImportParsedSample[];
  fx: ImportFxPair | null;
  plannedCovered: ImportPlannedCovered;
  /**
   * Rows dated on or before the opening date, duplicates included. Kept readable for compatibility
   * (TMMAPI-0191); the confirm screen reads `earlier`.
   */
  beforeOpeningCount: number;
  /** Absent on the wire when `beforeOpeningCount` is 0. */
  beforeOpeningFrom: string | null;
  beforeOpeningTo: string | null;
  /** TMMAPI-0149: does the mapping fit the file (null for a status this build doesn't know). */
  fit: ImportFit | null;
  summary: ImportSummary;
  /** Null when not served: the opening isn't pending, or the file suggests none. */
  suggestedOpening: ImportSuggestedOpening | null;
  suggestedOpeningIssue: ImportSuggestedOpeningIssue | null;
}

export interface ImportBatch {
  id: number;
  accountId: number;
  originalFilename: string;
  status: ImportBatchStatus;
  uploadedAt: string;
  committedAt: string | null;
  rowsTotal: number;
  rowsImported: number;
  rowsDuplicate: number;
  rowsRejected: number;
  confirmMatch: number;
  replaceSynthetic: number;
  planPartial: number;
  /** Rows SKIPPED because they were dated before the opening date (`includeBeforeOpening` false). */
  beforeOpeningCount: number;
  /**
   * Rows dated before the opening date that were IMPORTED because `includeBeforeOpening` was true
   * (TMMAPI-0123). Never non-zero beside `beforeOpeningCount`.
   */
  beforeOpeningImportedCount: number;
  beforeOpeningFrom: string | null;
  beforeOpeningTo: string | null;
  /** True when the commit imported the before-opening rows. */
  includeBeforeOpening: boolean;
  errorSummary: string | null;
  filePurgedAt: string | null;
  detected: ImportDetected | null;
  proposedMapping: ImportProposedMapping | null;
  suggestedProfileId: number | null;
  suggestedProfileName: string | null;
  detectedUnavailable: string | null;
}

export interface ImportBatchRow {
  rowNumber: number;
  /** Null for an outcome this build doesn't know. */
  outcome: ImportRowOutcome | null;
  rawLine: string;
  transactionId: number | null;
  duplicateOfTransactionId: number | null;
  rejectReason: string | null;
}

export interface ImportRowsPage {
  items: ImportBatchRow[];
  nextCursor: string | null;
}

export interface ImportPreviewBody {
  profileId?: number;
  /** TMMAPI-0149: an ACTIVE preset, in place of `profileId`. */
  presetId?: number;
  headerRow?: number;
  firstDataRow?: number;
  columnMap?: ColumnMap;
  amountMode?: AmountMode;
  outflowSign?: OutflowSign;
  dateFormat?: string;
  delimiter?: string;
  encoding?: string;
}

export interface ImportCommitBody {
  coveredThrough?: string;
  uncoveredPlanned?: UncoveredPlanned;
  saveProfile?: { name: string; scope: 'ACCOUNT' | 'OWNER' };
  /**
   * TMMAPI-0191: earlier history is imported by default; sent only as `false`, when the person unticks
   * "Include earlier history".
   */
  includeBeforeOpening?: boolean;
  /** TMMAPI-0191: sent only as `true`, when an `openingCheck` that `DIFFERS` is accepted. */
  acceptOpeningDifference?: boolean;
  /** TMMAPI-0149: the preset the batch was previewed with; the commit applies that mapping. */
  presetId?: number;
  /** TMMAPI-0160 U2: sets a pending account's opening balance (the suggestion, kept or edited). */
  opening?: { amountMinor: number; on: string };
}

/** Commit response — OpenAPI types this as `JsonNode`; read `historical` when present. */
export interface ImportCommitResult {
  historical: number | null;
}

const STATUSES: ReadonlySet<string> = new Set([
  'UPLOADED',
  'PREVIEWED',
  'COMMITTED',
  'FAILED',
  'DISCARDED',
]);
const OUTCOMES: ReadonlySet<string> = new Set([
  'IMPORTED',
  'DUPLICATE',
  'REJECTED',
  'BEFORE_OPENING',
]);
const AMOUNT_MODES: ReadonlySet<string> = new Set(['SIGNED', 'DEBIT_CREDIT']);
const OUTFLOW_SIGNS: ReadonlySet<string> = new Set(['NEGATIVE', 'POSITIVE']);
const SOURCES: ReadonlySet<string> = new Set(['PROFILE', 'PRESET', 'AI', 'HEURISTIC', 'NONE']);
const OPENING_SOURCES: ReadonlySet<string> = new Set([
  'USER',
  'FILE_STATEMENT',
  'RUNNING_BALANCE',
  'PROVIDER',
]);
const OPENING_ISSUES: ReadonlySet<string> = new Set(['BALANCE_MISMATCH', 'SOURCES_DISAGREE']);

/** Null for a source this build doesn't know. */
function normalizeSuggestedOpening(w: WireSuggestedOpening): ImportSuggestedOpening | null {
  if (!OPENING_SOURCES.has(w.source)) return null;
  return { amountMinor: w.amountMinor, on: w.on, source: w.source };
}

function normalizeColumnMap(w: WireColumnMap): ColumnMap {
  return {
    date: w.date ?? null,
    amount: w.amount ?? null,
    debit: w.debit ?? null,
    credit: w.credit ?? null,
    payee: w.payee ?? null,
    memo: w.memo ?? null,
    runningBalance: w.runningBalance ?? null,
    externalId: w.externalId ?? null,
  };
}

function normalizeDetected(w: WireDetected, preambleRows: number): ImportDetected {
  return {
    delimiter: w.delimiter,
    encoding: w.encoding,
    headerCandidates: w.headerCandidates,
    sampleRows: w.sampleRows,
    preambleRows,
    preambleSample: w.preambleSample,
    preambleFrom: w.preambleFrom,
  };
}

/** An unknown source reads NONE; an unknown amount mode or outflow sign reads the default. */
export function normalizeProposedMapping(w: WireMapping): ImportProposedMapping {
  return {
    source: SOURCES.has(w.source) ? w.source : 'NONE',
    confidence: w.confidence,
    headerRow: w.headerRow,
    firstDataRow: w.firstDataRow,
    columnMap: normalizeColumnMap(w.columnMap),
    amountMode: AMOUNT_MODES.has(w.amountMode) ? w.amountMode : 'SIGNED',
    outflowSign: OUTFLOW_SIGNS.has(w.outflowSign) ? w.outflowSign : 'NEGATIVE',
    dateFormat: w.dateFormat,
    notes: w.notes,
  };
}

export function normalizeUpload(w: WireUpload): ImportUpload {
  return {
    batchId: w.batchId,
    detected: normalizeDetected(w.detected, w.preambleRows),
    proposedMapping: normalizeProposedMapping(w.proposedMapping),
    suggestedProfileId: w.suggestedProfileId ?? null,
    suggestedProfileName: w.suggestedProfileName ?? null,
    suggestedPresetId: w.suggestedPresetId ?? null,
    suggestedPresetName: w.suggestedPresetName ?? null,
    fit: w.fit === undefined ? null : normalizeFit(w.fit),
    preview: w.preview === undefined ? null : normalizePreview(w.preview),
  };
}

/** Null for a status this build doesn't know. A reason keeps a newer code. */
export function normalizeFit(w: WireFit): ImportFit | null {
  if (w.status !== 'GOOD' && w.status !== 'POOR') return null;
  return {
    status: w.status,
    reasons: w.reasons.map((r) => ({
      code: r.code,
      count: r.count ?? null,
      detail: r.detail ?? null,
    })),
  };
}

function normalizeSummary(w: WireSummary): ImportSummary {
  return {
    firstDate: w.firstDate ?? null,
    lastDate: w.lastDate ?? null,
    inMinor: w.inMinor,
    outMinor: Math.abs(w.outMinor),
  };
}

const CHECK_STATUSES: ReadonlySet<string> = new Set(['MATCHES', 'DIFFERS', 'NO_BANK_FIGURE']);

function normalizeRowSpan(w: WireRowSpan): ImportRowSpan {
  return { count: w.count, from: w.from ?? null, to: w.to ?? null };
}

/** An unknown status reads as no check. */
function normalizeOpeningCheck(w: WireOpeningCheck): ImportOpeningCheck | null {
  if (!CHECK_STATUSES.has(w.status)) return null;
  return {
    proposedOn: w.proposedOn,
    proposedMinor: w.proposedMinor,
    bankStatedMinor: w.bankStatedMinor ?? null,
    status: w.status,
    differenceMinor: w.differenceMinor ?? null,
  };
}

export function normalizePreview(w: WirePreview): ImportPreview {
  return {
    rowsTotal: w.rowsTotal,
    wouldImport: w.wouldImport,
    newCount: w.newCount,
    earlier: normalizeRowSpan(w.earlier),
    newer: normalizeRowSpan(w.newer),
    openingCheck: w.openingCheck === undefined ? null : normalizeOpeningCheck(w.openingCheck),
    duplicates: w.duplicates,
    errors: w.errors.map((e) => ({ row: e.row, reason: e.reason })),
    sample: w.sample.map((s) => ({
      postedOn: s.postedOn,
      amountMinor: s.amountMinor,
      payee: s.payee,
      memo: s.memo ?? null,
      runningBalanceMinor: s.runningBalanceMinor ?? null,
      externalId: s.externalId ?? null,
    })),
    fx: w.fx === undefined ? null : normalizeFxPair(w.fx),
    plannedCovered: {
      count: w.plannedCovered.count,
      coveredThrough: w.plannedCovered.coveredThrough ?? null,
    },
    beforeOpeningCount: w.beforeOpeningCount,
    beforeOpeningFrom: w.beforeOpeningFrom ?? null,
    beforeOpeningTo: w.beforeOpeningTo ?? null,
    fit: normalizeFit(w.fit),
    summary: normalizeSummary(w.summary),
    suggestedOpening:
      w.suggestedOpening === undefined ? null : normalizeSuggestedOpening(w.suggestedOpening),
    suggestedOpeningIssue:
      w.suggestedOpeningIssue !== undefined && OPENING_ISSUES.has(w.suggestedOpeningIssue)
        ? w.suggestedOpeningIssue
        : null,
  };
}

function normalizeFxPair(w: WireFxPair): ImportFxPair {
  return { base: w.base, quote: w.quote, rate: w.rate, asOf: w.asOf, fxRateId: w.fxRateId };
}

/** An unknown status reads UPLOADED. */
export function normalizeBatch(w: WireBatch): ImportBatch {
  return {
    id: w.id,
    accountId: w.accountId,
    originalFilename: w.originalFilename,
    status: STATUSES.has(w.status) ? w.status : 'UPLOADED',
    uploadedAt: w.uploadedAt,
    committedAt: w.committedAt ?? null,
    rowsTotal: w.rowsTotal,
    rowsImported: w.rowsImported,
    rowsDuplicate: w.rowsDuplicate,
    rowsRejected: w.rowsRejected,
    confirmMatch: w.confirmMatch,
    replaceSynthetic: w.replaceSynthetic,
    planPartial: w.planPartial,
    beforeOpeningCount: w.beforeOpeningCount,
    beforeOpeningImportedCount: w.beforeOpeningImportedCount,
    beforeOpeningFrom: w.beforeOpeningFrom ?? null,
    beforeOpeningTo: w.beforeOpeningTo ?? null,
    includeBeforeOpening: w.includeBeforeOpening,
    errorSummary: w.errorSummary ?? null,
    filePurgedAt: w.filePurgedAt ?? null,
    detected: w.detected === undefined ? null : normalizeDetected(w.detected, w.preambleRows),
    proposedMapping:
      w.proposedMapping === undefined ? null : normalizeProposedMapping(w.proposedMapping),
    suggestedProfileId: w.suggestedProfileId ?? null,
    suggestedProfileName: w.suggestedProfileName ?? null,
    detectedUnavailable: w.detectedUnavailable ?? null,
  };
}

/** An outcome this build doesn't know is null. */
function normalizeRow(w: WireRow): ImportBatchRow {
  return {
    rowNumber: w.rowNumber,
    outcome: OUTCOMES.has(w.outcome) ? w.outcome : null,
    rawLine: w.rawLine,
    transactionId: w.transactionId ?? null,
    duplicateOfTransactionId: w.duplicateOfTransactionId ?? null,
    rejectReason: w.rejectReason ?? null,
  };
}

function columnMapWire(map: ColumnMap): WireColumnMap {
  const out: WireColumnMap = {};
  for (const [key, value] of Object.entries(map)) {
    if (typeof value === 'number') out[key as keyof WireColumnMap] = value;
  }
  return out;
}

/** Only the keys the caller set; every field is optional. */
function previewWire(body: ImportPreviewBody): ImportPreviewRequest {
  return {
    ...(body.profileId !== undefined ? { profileId: body.profileId } : {}),
    ...(body.presetId !== undefined ? { presetId: body.presetId } : {}),
    ...(body.headerRow !== undefined ? { headerRow: body.headerRow } : {}),
    ...(body.firstDataRow !== undefined ? { firstDataRow: body.firstDataRow } : {}),
    ...(body.columnMap !== undefined ? { columnMap: columnMapWire(body.columnMap) } : {}),
    ...(body.amountMode !== undefined ? { amountMode: body.amountMode } : {}),
    ...(body.outflowSign !== undefined ? { outflowSign: body.outflowSign } : {}),
    ...(body.dateFormat !== undefined ? { dateFormat: body.dateFormat } : {}),
    ...(body.delimiter !== undefined ? { delimiter: body.delimiter } : {}),
    ...(body.encoding !== undefined ? { encoding: body.encoding } : {}),
  };
}

/**
 * Only the keys the caller set; `includeBeforeOpening` only when false (the server default is true,
 * TMMAPI-0191) and `acceptOpeningDifference` only when true.
 */
function commitWire(body: ImportCommitBody): ImportCommitRequest {
  return {
    ...(body.coveredThrough !== undefined ? { coveredThrough: body.coveredThrough } : {}),
    ...(body.uncoveredPlanned !== undefined ? { uncoveredPlanned: body.uncoveredPlanned } : {}),
    ...(body.saveProfile !== undefined ? { saveProfile: body.saveProfile } : {}),
    ...(body.includeBeforeOpening === false ? { includeBeforeOpening: false } : {}),
    ...(body.acceptOpeningDifference === true ? { acceptOpeningDifference: true } : {}),
    ...(body.presetId !== undefined ? { presetId: body.presetId } : {}),
    ...(body.opening !== undefined ? { opening: body.opening } : {}),
  };
}

/** The commit answer is an untyped `JsonNode`; `historical` is read when it is a number. */
function historicalOf(wire: unknown): number | null {
  if (typeof wire !== 'object' || wire === null || !('historical' in wire)) return null;
  return typeof wire.historical === 'number' ? wire.historical : null;
}

export const importsApi = {
  /** POST /v1/me/accounts/{accountId}/imports — multipart `file`. */
  async upload(
    accountId: number,
    file: File,
    onUploadProgress?: AxiosRequestConfig['onUploadProgress'],
  ): Promise<ImportUpload> {
    const form = new FormData();
    form.append('file', file);
    return normalizeUpload(
      await request<WireUpload>('POST', `/v1/me/accounts/${accountId}/imports`, {
        form,
        onUploadProgress,
      }),
    );
  },

  async suggestMapping(accountId: number, batchId: number): Promise<ImportProposedMapping> {
    return normalizeProposedMapping(
      await request<WireMapping>(
        'POST',
        `/v1/me/accounts/${accountId}/imports/${batchId}/suggest-mapping`,
      ),
    );
  },

  async preview(
    accountId: number,
    batchId: number,
    body: ImportPreviewBody,
  ): Promise<ImportPreview> {
    return normalizePreview(
      await request<WirePreview>(
        'POST',
        `/v1/me/accounts/${accountId}/imports/${batchId}/preview`,
        {
          json: previewWire(body),
        },
      ),
    );
  },

  async commit(
    accountId: number,
    batchId: number,
    body: ImportCommitBody,
    idempotencyKey: string,
  ): Promise<ImportCommitResult> {
    const wire = await request<unknown>(
      'POST',
      `/v1/me/accounts/${accountId}/imports/${batchId}/commit`,
      {
        json: commitWire(body),
        idempotencyKey,
      },
    );
    return { historical: historicalOf(wire) };
  },

  async list(accountId: number): Promise<ImportBatch[]> {
    const wire = await request<WireBatch[]>('GET', `/v1/me/accounts/${accountId}/imports`);
    return wire.map(normalizeBatch);
  },

  async get(accountId: number, batchId: number): Promise<ImportBatch> {
    return normalizeBatch(
      await request<WireBatch>('GET', `/v1/me/accounts/${accountId}/imports/${batchId}`),
    );
  },

  async rows(
    accountId: number,
    batchId: number,
    query: { outcome?: ImportRowOutcome; cursor?: string; limit?: number } = {},
  ): Promise<ImportRowsPage> {
    const wire = await request<WireRowPage>(
      'GET',
      `/v1/me/accounts/${accountId}/imports/${batchId}/rows`,
      { query },
    );
    return { items: wire.items.map(normalizeRow), nextCursor: wire.page.nextCursor };
  },

  async discard(accountId: number, batchId: number): Promise<void> {
    await request<void>('DELETE', `/v1/me/accounts/${accountId}/imports/${batchId}`);
  },
};
